import React from 'react';
import PropTypes from 'prop-types';

import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton';

import GameStateTypes from 'bdata-sns-shared/game/types/GameStateTypes';
import useRules from 'bdata-sns-shared/hooks/useRules';
import getTranslation from 'bdata-sns-shared/utils/getTranslation';
import getDueUpState from 'bdata-sns-shared/utils/getDueUpState';
import { generateMatchups, PITCHING } from 'bdata-sns-shared/utils/playerMatchups';
import { cardTypes } from 'bdata-sns-shared/utils/productButtonConfigs';

import {
    PlayerMatchupLayerWrapper,
    PlayerMatchupHeaderWrapper,
    PlayerMatchupTeamWrapper,
    TeamLiveWrapper,
    PlayerMatchupWrapper,
    PlayerMatchupContentWrapper,
    DueUpMatchupWrapper
} from './PlayerMatchupLayer.style';
import { PlayerMatchupScoresOverride } from './PlayerMatchupOverride.style';
import { HeadshotTypes } from '@mlb/react-patterns/components/Headshot/headshot.types';

const generateHeader = ({
    gameState,
    isPostponed,
    dictionary,
    linescore,
    awayTeamAbbrev,
    homeTeamAbbrev,
    isLoading,
    isWarmup,
    awayTeamShortName,
    homeTeamShortName,
    dueUpConfig,
    mode
}) => {
    const LoadingContent = (
        <PlayerMatchupHeaderWrapper>
            <Skeleton height={ 10 } width={ 40 } />
            <Skeleton height={ 10 } width={ 40 } />
        </PlayerMatchupHeaderWrapper>
    );

    if (gameState === GameStateTypes.PREVIEW || isPostponed || isWarmup) {
        if (isLoading) {
            return LoadingContent;
        }
        return (
            <PlayerMatchupHeaderWrapper>
                <PlayerMatchupTeamWrapper aria-hidden='true' mode={ mode }>{ awayTeamShortName }</PlayerMatchupTeamWrapper>
                <PlayerMatchupTeamWrapper aria-hidden='true' mode={ mode }>{ homeTeamShortName }</PlayerMatchupTeamWrapper>
            </PlayerMatchupHeaderWrapper>
        );
    } else if (dueUpConfig.isBetweenInnings && dueUpConfig.hasCompletePlayerData) {
        if (isLoading) {
            return null;
        }
        const inningState = linescore.inningState;
        const dueUpText = getTranslation(dictionary, 'due_up', 'Due Up');
        const teamName = inningState === 'Middle' ? homeTeamAbbrev : awayTeamAbbrev;

        return (
            <PlayerMatchupHeaderWrapper>
                <TeamLiveWrapper inline={ mode === cardTypes.MINI_SB }>
                    <div aria-hidden='true'>{dueUpText}</div>
                    <PlayerMatchupTeamWrapper aria-hidden='true' mode={ mode }>{teamName}</PlayerMatchupTeamWrapper>
                </TeamLiveWrapper>
            </PlayerMatchupHeaderWrapper>
        );
    } else if (gameState === GameStateTypes.LIVE) {
        if (isLoading) {
            return null;
        }

        const isTopInning = linescore.isTopInning;
        const pitchingTitle = (mode === cardTypes.MINI_SB) ? getTranslation(dictionary, 'pitching_abbrev', 'P') : getTranslation(dictionary, PITCHING, 'Pitching');
        const atBatTitle = (mode === cardTypes.MINI_SB) ? getTranslation(dictionary, 'at_bat_abbrev', 'AB') : getTranslation(dictionary, 'at_bat', 'At Bat');

        // mobile design does not show team during live, but ticket asks for it https://baseball.atlassian.net/browse/BDFED-3303
        return (
            <PlayerMatchupHeaderWrapper>
                <TeamLiveWrapper>
                    <div aria-hidden='true'>{ pitchingTitle }</div>
                    <PlayerMatchupTeamWrapper aria-hidden='true' mode={ mode }>{ isTopInning ? homeTeamAbbrev : awayTeamAbbrev }</PlayerMatchupTeamWrapper>
                </TeamLiveWrapper>
                <TeamLiveWrapper>
                    <div aria-hidden='true'>{ atBatTitle }</div>
                    <PlayerMatchupTeamWrapper aria-hidden='true' mode={ mode }>{ isTopInning ? awayTeamAbbrev : homeTeamAbbrev }</PlayerMatchupTeamWrapper>
                </TeamLiveWrapper>
            </PlayerMatchupHeaderWrapper>
        );
    }
};

const PlayerMatchupLayer = ({
    gameState,
    abstract,
    dictionary,
    homeTeam,
    awayTeam,
    linescore,
    decisions,
    isLoading,
    urlRoot,
    headshotTypes = HeadshotTypes.SPOTS,
    awayTeamShortName,
    homeTeamShortName,
    awayTeamAbbrev,
    homeTeamAbbrev,
    showHeadShot = true,
    mode = cardTypes.SCOREBOARD,
    playerStatsInline = false,
    useStackedLayout = false
}) => {
    const { disablePlayerPageLink = false } = useRules();
    const { isPostponed, isWarmup, isSuspended, isLive, isPostSeasonReady } = abstract;

    const {
        inningState,
        offense
    } = linescore;

    const dueUpConfig = getDueUpState(isLive, isWarmup, isSuspended, inningState, offense);

    const Header = generateHeader({
        gameState,
        isPostponed,
        dictionary,
        linescore,
        awayTeamAbbrev,
        homeTeamAbbrev,
        isLoading,
        isWarmup,
        awayTeamShortName,
        homeTeamShortName,
        dueUpConfig,
        mode
    });
    const PlayerMatchups = generateMatchups({
        homeTeam,
        awayTeam,
        dictionary,
        gameState,
        linescore,
        decisions,
        isLoading,
        urlRoot,
        headshotTypes,
        abstract,
        simpleInline: false,
        component: PlayerMatchupScoresOverride,
        dueUpConfig,
        showHeadShot,
        playerStatsInline,
        mode,
        disablePlayerPageLink
    });

    const { isBetweenInnings, hasCompletePlayerData } = dueUpConfig;

    const InnerWrapper = isBetweenInnings && hasCompletePlayerData ? DueUpMatchupWrapper : PlayerMatchupWrapper;

    return (
        <PlayerMatchupLayerWrapper
            gameStateFinal={ gameState === GameStateTypes.FINAL }
            isPostponed={ isPostponed }
            className="matchup-wrapper"
            isPostSeason={ isPostSeasonReady }
            mode={ mode }
        >
            <PlayerMatchupContentWrapper>
                { Header }
                <InnerWrapper mode={ mode } useStackedLayout={ useStackedLayout }>
                    { PlayerMatchups }
                </InnerWrapper>
            </PlayerMatchupContentWrapper>
        </PlayerMatchupLayerWrapper>
    );
};

PlayerMatchupLayer.propTypes = {
    isMobile: PropTypes.bool,
    gameState: PropTypes.oneOf(Object.values(GameStateTypes)),
    abstract: PropTypes.object,
    dictionary: PropTypes.object,
    homeTeam: PropTypes.object,
    awayTeam: PropTypes.object,
    linescore: PropTypes.object,
    decisions: PropTypes.object,
    isLoading: PropTypes.bool,
    awayTeamAbbrev: PropTypes.string,
    homeTeamAbbrev: PropTypes.string,
    awayTeamShortName: PropTypes.string,
    homeTeamShortName: PropTypes.string,
    urlRoot: PropTypes.string,
    showHeadShot: PropTypes.bool,
    mode: PropTypes.string,
    useStackedLayout: PropTypes.bool,
    playerStatsInline: PropTypes.bool,
    headshotTypes: PropTypes.oneOf(Object.values(HeadshotTypes))
};

export default PlayerMatchupLayer;
